import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { SyllabusQuery } from '@examdojo/category';
import {
  DerivativeQuestionReviewType,
  QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION,
  REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION,
  ReferenceQuestionReviewType,
  ReviewPermissionsQuery,
} from '@examdojo/review';
import { RootUrlParts, SyllabusUrlParts, ValidateUrlParts } from '../app.model';
import { ActiveModeService } from '../storage/active-mode-storage-manager';
import { SYLLABUS_SLUG_PARAM } from './validation-routing.model';

function getSyllabusSlugFromRoute(route: ActivatedRouteSnapshot): string | null {
  // Traverse up the route tree to find syllabusSlug parameter
  let currentRoute: ActivatedRouteSnapshot | null = route;

  while (currentRoute) {
    const syllabusSlug = currentRoute.paramMap.get(SYLLABUS_SLUG_PARAM);
    if (syllabusSlug) {
      return syllabusSlug;
    }
    currentRoute = currentRoute.parent;
  }

  return null;
}

function findFirstAvailableMode(
  reviewPermissionsQuery: ReviewPermissionsQuery,
  syllabusId: number,
  syllabusSlug: string,
): string | null {
  for (const mode of Object.values(DerivativeQuestionReviewType)) {
    const permission = QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[mode];
    if (reviewPermissionsQuery.hasReviewPermission(syllabusId, permission)) {
      return `/${RootUrlParts.Syllabus}/${syllabusSlug}/${SyllabusUrlParts.Validate}/${ValidateUrlParts.DerivativeQuestion}/${mode}`;
    }
  }

  for (const mode of Object.values(ReferenceQuestionReviewType)) {
    const permission = REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[mode];
    if (reviewPermissionsQuery.hasReviewPermission(syllabusId, permission)) {
      return `/${RootUrlParts.Syllabus}/${syllabusSlug}/${SyllabusUrlParts.Validate}/${ValidateUrlParts.ReferenceQuestion}/${mode}`;
    }
  }

  return null;
}

export const redirectToLastActiveQuestionTypeGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const activeModeService = inject(ActiveModeService);
  const reviewPermissionsQuery = inject(ReviewPermissionsQuery);
  const syllabusQuery = inject(SyllabusQuery);

  const syllabusSlug = getSyllabusSlugFromRoute(route);
  const syllabus = syllabusSlug && syllabusQuery.getBySlug(syllabusSlug);

  if (!syllabusSlug || !syllabus) {
    return router.createUrlTree([`/${RootUrlParts.Overview}`]);
  }

  const lastDerivativeMode = activeModeService.getLastDerivativeQuestionMode();
  const lastReferenceMode = activeModeService.getLastReferenceQuestionMode();

  const canAccessDerivativeMode =
    lastDerivativeMode &&
    reviewPermissionsQuery.hasReviewPermission(
      syllabus.id,
      QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[lastDerivativeMode],
    );

  const canAccessReferenceMode =
    lastReferenceMode &&
    reviewPermissionsQuery.hasReviewPermission(
      syllabus.id,
      REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[lastReferenceMode],
    );

  if (canAccessDerivativeMode) {
    return router.createUrlTree([`/syllabus/${syllabusSlug}/validate/derivative-question/${lastDerivativeMode}`]);
  } else if (canAccessReferenceMode) {
    return router.createUrlTree([`/syllabus/${syllabusSlug}/validate/reference-question/${lastReferenceMode}`]);
  } else {
    const fallbackRoute = findFirstAvailableMode(reviewPermissionsQuery, syllabus.id, syllabusSlug);
    if (fallbackRoute) {
      return router.createUrlTree([fallbackRoute]);
    }
    return router.createUrlTree([`/${RootUrlParts.Overview}`]);
  }
};

export const redirectToLastDerivativeQuestionModeGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const activeModeService = inject(ActiveModeService);
  const reviewPermissionsQuery = inject(ReviewPermissionsQuery);
  const syllabusQuery = inject(SyllabusQuery);

  const syllabusSlug = getSyllabusSlugFromRoute(route);
  const syllabus = syllabusSlug && syllabusQuery.getBySlug(syllabusSlug);

  if (!syllabusSlug || !syllabus) {
    return router.createUrlTree([`/${RootUrlParts.Overview}`]);
  }

  const lastMode = activeModeService.getLastDerivativeQuestionMode();
  const canAccessLastMode =
    lastMode &&
    reviewPermissionsQuery.hasReviewPermission(syllabus.id, QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[lastMode]);

  if (canAccessLastMode) {
    return router.createUrlTree([`/syllabus/${syllabusSlug}/validate/derivative-question/${lastMode}`]);
  }

  const fallbackMode = Object.values(DerivativeQuestionReviewType).find((mode) =>
    reviewPermissionsQuery.hasReviewPermission(syllabus.id, QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[mode]),
  );

  if (fallbackMode) {
    return router.createUrlTree([`/syllabus/${syllabusSlug}/validate/derivative-question/${fallbackMode}`]);
  }

  return router.createUrlTree([`/${RootUrlParts.Overview}`]);
};

export const redirectToLastReferenceQuestionModeGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const activeModeService = inject(ActiveModeService);
  const reviewPermissionsQuery = inject(ReviewPermissionsQuery);
  const syllabusQuery = inject(SyllabusQuery);

  const syllabusSlug = getSyllabusSlugFromRoute(route);
  const syllabus = syllabusSlug && syllabusQuery.getBySlug(syllabusSlug);

  if (!syllabusSlug || !syllabus) {
    return router.createUrlTree([`/${RootUrlParts.Overview}`]);
  }

  const lastMode = activeModeService.getLastReferenceQuestionMode();
  const canAccessLastMode =
    lastMode &&
    reviewPermissionsQuery.hasReviewPermission(
      syllabus.id,
      REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[lastMode],
    );

  if (canAccessLastMode) {
    return router.createUrlTree([`/syllabus/${syllabusSlug}/validate/reference-question/${lastMode}`]);
  }

  const fallbackMode = Object.values(ReferenceQuestionReviewType).find((mode) =>
    reviewPermissionsQuery.hasReviewPermission(syllabus.id, REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION[mode]),
  );

  if (fallbackMode) {
    return router.createUrlTree([`/syllabus/${syllabusSlug}/validate/reference-question/${fallbackMode}`]);
  }

  return router.createUrlTree([`/${RootUrlParts.Overview}`]);
};
